import { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { unqueuePupCard } from '../../redux/pupApi';
import { playCardSnap } from '../../utilities/sounds';
import PupCard from './PupCard';
import { isConditionalPupCard, pupConditionMet } from './pupConditions';
import './PupAutoPlayBox.css';

// Movement (px) before a press counts as a drag rather than a click.
const DRAG_THRESHOLD_PX = 6;

// The AUTO-PLAY BOX — the left-hand twin of the exchange staging box (PupStagingBox). Drag ANY
// pup card from the dock (PupHand) into here and the SERVER plays it at the next moment it is
// legally playable: drop a "Power-Up (Turn)" during pre-flop and it fires at the Turn. If the
// card is already legal when it lands here, it fires immediately. The pile is CROSS-ROUND, so a
// card whose effect has a content precondition (Demonic Ritual's three 6s) simply waits, round
// after round, until the condition is met.
//
// Cards eligible at the same moment resolve serially in dealer-left seat order (and in box order
// within a seat). A card that opens a pick window still opens it: the normal overlay appears and
// the player answers it live before arbitration continues.
//
// The pile is private and authoritative server-side (its own QUEUED card zone, mirrored into
// state.pup.queued[roomId] as a codes array); this box is a mirror. Rendered inside the same
// .game-table as PupHand so `closest` resolves the right table in split-screen. The container
// itself is pointer-events: none (its rect is the drop target PupHand hit-tests); only the queued
// cards catch the pointer, for the drag-back-out gesture.
const PupAutoPlayBox = ({ roomId, conditionContext }) => {
  const dispatch = useDispatch();
  const queuedCodes = useSelector((state) => state.pup.queued[roomId]) || [];
  // Capture the card code as well as its visual index. A private autoplay push can shrink or
  // reorder this list while the pointer is down; re-reading queuedCodes[idx] at release could
  // otherwise unqueue a different card.
  const [drag, setDrag] = useState(null);
  const [busyIdx, setBusyIdx] = useState(null);
  const [error, setError] = useState(null);
  const rootRef = useRef(null);

  const isOverDock = (x, y) => {
    const dock = rootRef.current?.closest('.game-table')?.querySelector('.pup-hand');
    if (!dock) return false;
    const r = dock.getBoundingClientRect();
    return x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
  };

  const handleUnqueue = async (code, idx) => {
    setBusyIdx(idx);
    setError(null);
    try {
      await dispatch(unqueuePupCard({ roomId, card: code })).unwrap();
      playCardSnap();
    } catch (e) {
      setError(typeof e === 'string' && e ? e : 'Could not remove that card');
      setTimeout(() => setError(null), 4000);
    } finally {
      setBusyIdx(null);
    }
  };

  const onPointerDown = (e, idx, code) => {
    if (busyIdx != null) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    setDrag({ idx, code, x: e.clientX, y: e.clientY,
      startX: e.clientX, startY: e.clientY, active: false });
  };
  const onPointerMove = (e) => {
    if (!drag) return;
    const { clientX: x, clientY: y } = e;
    setDrag((d) => d && {
      ...d, x, y,
      active: d.active || Math.hypot(x - d.startX, y - d.startY) > DRAG_THRESHOLD_PX,
    });
  };
  const onPointerUp = (e) => {
    const d = drag;
    setDrag(null);
    if (!d || !d.active) return;
    const liveIdx = queuedCodes.indexOf(d.code);
    if (liveIdx >= 0 && isOverDock(e.clientX, e.clientY)) {
      handleUnqueue(d.code, liveIdx);
    }
    // Released elsewhere: keep it queued (the ghost just unmounts).
  };

  const overDock = drag?.active ? isOverDock(drag.x, drag.y) : false;

  return (
    <div className="pup-autoplay-box" ref={rootRef}>
      {error && <div className="pup-hand-error pup-autoplay-error">{error}</div>}
      <div className="pup-autoplay-title">
        <span>Auto-play</span>
        <span className="pup-autoplay-count">
          {queuedCodes.length ? `⏱ ${queuedCodes.length}` : ''}
        </span>
      </div>
      {queuedCodes.length === 0 ? (
        <div className="pup-autoplay-empty">
          Drag a card here to play it
          <br />
          at its next legal moment
        </div>
      ) : (
        <div className="pup-autoplay-cards">
          {queuedCodes.map((code, idx) => {
            // A conditional card wears the red "about to fire" ring the moment its content
            // precondition is satisfied — the same language the dock uses. (The stage/phase half
            // is the server's; here the ring just answers "is the condition met yet?".)
            const armed = conditionContext && isConditionalPupCard(code)
              && pupConditionMet(code, conditionContext);
            return (
              <div
                key={`${code}-${idx}`}
                className={
                  'pup-autoplay-card'
                  + (busyIdx === idx ? ' pup-autoplay-card-busy' : '')
                  + (drag?.active && drag.idx === idx ? ' pup-autoplay-card-dragged' : '')
                  + (armed ? ' pup-autoplay-card-armed' : '')
                }
                onPointerDown={(e) => onPointerDown(e, idx, code)}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={() => setDrag(null)}
                onContextMenu={(e) => {
                  e.preventDefault();
                  if (busyIdx == null) handleUnqueue(code, idx);
                }}
                title="Drag to your hand or right-click to remove"
              >
                <div className="pup-autoplay-scale">
                  <PupCard code={code} roomId={roomId} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Drag ghost riding the pointer while pulling a card back toward the dock. Reuses the
          dock's .pup-drag-ghost styling (PupHand.css is bundled globally). */}
      {drag?.active && queuedCodes.includes(drag.code) && createPortal(
        <div
          className={`pup-drag-ghost${overDock ? ' pup-drag-ghost-over' : ''}`}
          style={{ left: drag.x, top: drag.y }}
        >
          <PupCard code={drag.code} roomId={roomId} />
          <div className="pup-drag-label">{
            overDock ? 'Release to return to hand' : 'Drag to your hand'
          }</div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default PupAutoPlayBox;
